/**
 * A-36 — what the review step calls a change. Kept out of the component so it can be
 * tested on its own; the server decides what is saved, this decides what is shown first.
 */

export type DueRule = { type: 'NET_DAYS'; days: number } | { type: 'MONTH_END'; graceDays: number };
export interface Policy {
  defaultBudget: string | null;
  budgetVariesPerRetailer: boolean;
  budgetReleaseOn: 'SALE' | 'PAYMENT';
  /**
   * BG-04 — the portal's tolerance band on the ordering ceiling, as a decimal percentage string
   * (NF-04: it multiplies money). A retailer may be given their own on A-04; this is what an
   * account with none takes. '0' is valid and means this portal grants no tolerance.
   */
  budgetTolerancePct: string;
  payableDueRule: DueRule;
  taxTreatment: 'MANUAL';
  leadTime: { minDays: number; maxDays: number };
  standardSpecification: { diamond: string; gold: string } | null;
  /** AN-01 ⚙️ */
  slowMovingDays: number;
  /** AN-03 ⚙️ — whole amounts, as strings (NF-04), ascending. */
  priceBands: string[];
  /** AN-13 ⚙️ — the month a reporting quarter starts in, 1–12. D98 ⏸; a setting, never a country test (D46). */
  reportingQuarterStartMonth: number;
  /** CN-09 / D20, CN-10 / D39 D81, CN-11 / D40 — open decisions, held as settings. */
  recallTrigger: RecallTrigger;
  lossDefaultOutcome: 'CHARGED' | 'INSURANCE_CLAIM' | 'WRITTEN_OFF' | null;
  verificationCycleDays: number | null;
}
export type RecallTrigger = { type: 'NONE' } | { type: 'ALERT'; afterDays: number } | { type: 'RECALL'; afterDays: number };
export type Key = keyof Policy;

export const KEYS: Key[] = [
  'defaultBudget', 'budgetVariesPerRetailer', 'budgetTolerancePct', 'budgetReleaseOn', 'payableDueRule',
  'taxTreatment', 'leadTime', 'standardSpecification', 'slowMovingDays', 'priceBands',
  'taxTreatment', 'leadTime', 'standardSpecification',
  'reportingQuarterStartMonth',
  'recallTrigger', 'lossDefaultOutcome', 'verificationCycleDays',
];

/** What Admin types for AN-03's bands — "500, 1000, 2500" — as the list the API validates. */
export const parseBands = (raw: string): string[] => raw.split(/[\s,]+/).map((b) => b.trim()).filter((b) => b !== '');

/** The same rule twice is the same rule, whatever order its keys arrive in. */
const canonical = (v: unknown): string => JSON.stringify(v, (_k, x: unknown) => (x && typeof x === 'object' && !Array.isArray(x)
  ? Object.fromEntries(Object.keys(x).sort().map((k) => [k, (x as Record<string, unknown>)[k]])) : x));

/** "40000" and "40000.0000" are one amount. Only zeros AFTER a decimal point are dropped — "40000" is not "4". */
const amountKey = (s: string) => (s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s);
export const sameBudget = (a: string | null, b: string | null) =>
  a === b || (a !== null && b !== null && amountKey(a.trim()) === amountKey(b.trim()));

export function changed(before: Policy, after: Policy): Key[] {
  return KEYS.filter((k) => {
    if (k === 'defaultBudget') return !sameBudget(before.defaultBudget, after.defaultBudget);
    // BG-04 — "5" and "5.0000" are one percentage, as "40000" and "40000.0000" are one amount.
    // Without this, reopening the form and saving reports a change nobody made.
    if (k === 'budgetTolerancePct') return !sameBudget(before.budgetTolerancePct, after.budgetTolerancePct);
    return canonical(before[k]) !== canonical(after[k]);
  });
}

